import { Client, ColorResolvable } from "discord.js";
import { BotOptions, Emoji, PlainBotOptions } from "./typings/bot";
import { crop, highlight, isTypescript } from "@backs/utils";
import { magenta, green, yellow, red, bold, blackBright, bgGreen, bgYellow, bgRed, italic } from "colorette";
import { glob } from "glob";
import { Event } from "./structures/events";
import { AnyCommand } from "./typings/commands";
import { Plugin } from "./structures";
import path from "path";
import isGlob from "is-glob";
import createLogger, { TypedLogger } from "@backs/logger";
import ConfigParser from "@backs/config-parser";
import CommandManager from "./managers/commands";
import PluginManager from "./managers/plugins";
import MultiPageEmbedManager from "./managers/multipageembed";

export default async function createClient<
    Configs extends Record<string, any>,
    Emojis extends Record<string, Emoji>,
    Colors extends Record<string, ColorResolvable>,
>(options: BotOptions<Configs, Emojis, Colors>) {
    const configParser = new ConfigParser<Configs>({
        ...options.configOptions,
        logging: {
            debug: process.env.NODE_ENV === "debug",
        },
        start: false,
    });
    await configParser.start();
    const parsedOptions = parseOptions(configParser.configs, options);
    return new Bot(parsedOptions, configParser, options);
}

export class Bot<
    Configs extends Record<string, any>,
    Emojis extends Record<string, Emoji>,
    Colors extends Record<string, ColorResolvable>,
> extends Client<true> {
    private configParser: ConfigParser<Configs>;
    botOptions: PlainBotOptions<Configs, Emojis, Colors>;
    private _botOptions: BotOptions<Configs, Emojis, Colors>;
    logger: TypedLogger<["error", "warn", "debug", "log", "info"]>;
    commands: CommandManager<Configs, Emojis, Colors>;
    plugins: PluginManager<Configs, Emojis, Colors>;
    multiPageEmbed: MultiPageEmbedManager<Configs, Emojis, Colors>;

    get configs() {
        return this.configParser.configs;
    }

    get clientEmojis(): Emojis {
        const emojis = this._botOptions.emojis;
        if (typeof emojis === "function") return emojis(this.configs);
        else return (this.botOptions.emojis as Emojis) ?? {};
    }

    get clientColors(): Colors {
        const colors = this._botOptions.colors;
        if (typeof colors === "function") return colors(this.configs);
        else return (this.botOptions.colors as Colors) ?? {};
    }

    constructor(
        options: PlainBotOptions<Configs, Emojis, Colors>,
        configParser: ConfigParser<Configs>,
        BotBaseOptions: BotOptions<Configs, Emojis, Colors>,
    ) {
        options.clientOptions.ws ??= {};
        // @ts-expect-error - DiscordJS doesnt have these typings
        options.clientOptions.ws.properties ??= {};
        // @ts-expect-error - DiscordJS doesnt have these typings
        options.clientOptions.ws.properties.$browser = options.clientOptions.browser;

        super(options.clientOptions);
        this.configParser = configParser;
        this.botOptions = options;
        this._botOptions = BotBaseOptions;
        this.botOptions.debug ??= process.env.NODE_ENV === "debug";
        this.botOptions.loggerOptions ??= {};
        this.botOptions.loggerOptions.log ??= {} as Record<
            "error" | "warn" | "debug" | "log" | "info",
            boolean | (() => boolean)
        >;
        this.botOptions.loggerOptions.log.debug ??= this.botOptions.debug;

        this.logger = createLogger(this.botOptions.loggerOptions);
        this.commands = new CommandManager(this);
        this.plugins = new PluginManager(this);
        this.multiPageEmbed = new MultiPageEmbedManager(this);

        process.on("uncaughtException", (err) => {
            this.logger.error(err);
        });
        process.on("unhandledRejection", (err, promise) => {
            this.logger.error(err);
            promise.catch((err) => this.logger.error(err));
        });
    }

    emoji(name: keyof Emojis) {
        return this.clientEmojis[name];
    }

    color(name: keyof Colors) {
        return this.clientColors[name];
    }

    override async login(token?: string) {
        if (!this.configParser.isStarted) await this.configParser.start();
        token =
            token ??
            process.env.TOKEN ??
            process.env.DISCORD_TOKEN ??
            process.env.BOT_TOKEN ??
            process.env.DISCORD_BOT_TOKEN ??
            this.botOptions.token;

        await this.loadPlugins();
        await this.loadEvents();
        await this.loadCommands();
        if (this.botOptions.debug) this.on("debug", (info) => this.logger.debug(highlight(info, magenta)));
        this.once("ready", () => this.logger.log("Client logged in as %bl_i.", `@${this.user!.tag}`));

        const loggedIn = await super.login(token).catch((err) => {
            this.logger.error("Failed to login to Discord. %r", "Exiting...");
            this.logger.error(err);
            process.exit(1);
        });
        await this.application?.fetch();
        this.logger.debug(
            `Logged in.\n\t%m ${this.guilds.cache.size}\n\t%m ${this.ws.shards.size}`,
            "Guilds:",
            "Shards:",
        );

        return loggedIn;
    }

    override async destroy() {
        this.logger.debug("Stopping config parser...");
        this.configParser.stop();
        this.logger.debug("Clearing commands...");
        this.commands.destroy();
        this.logger.debug("Destroying client...");
        await super.destroy();
    }

    private async load<T>(filePath: string): Promise<{
        loaded: T[];
        files: Record<
            string,
            {
                status: string;
                index?: number;
                reason?: string;
            }
        >;
    }> {
        if (isGlob(filePath)) {
            const globbedFiles = await glob(filePath);
            const toLoad: string[] = [];
            const files = {};
            const loaded: T[] = [];

            for (let file of globbedFiles) {
                if (!path.isAbsolute(file)) file = path.join(path.relative(__dirname, process.cwd()), file);

                if ((path.extname(file) !== ".ts" && isTypescript) || (path.extname(file) !== ".js" && !isTypescript)) {
                    files[file] = {
                        status: "skipped",
                        reason: isTypescript ? "JS file in TS environment" : "TS file in JS environment",
                    };
                    continue;
                }

                toLoad.push(file);
            }

            for (const file of toLoad) {
                try {
                    const loadedFiles = require(file).default;
                    loaded.push(loadedFiles);
                    files[file] = {
                        status: "loaded",
                        index: loaded.length - 1,
                    };
                } catch (err) {
                    this.logger.error(err);
                    files[file] = {
                        status: "errored",
                        reason: crop(err.message, 50),
                    };
                }
            }

            return {
                loaded,
                files,
            };
        } else {
            if (!path.isAbsolute(filePath)) filePath = path.relative(__dirname, path.join(process.cwd(), filePath));
            if (
                (path.extname(filePath) !== ".ts" && isTypescript) ||
                (path.extname(filePath) !== ".js" && !isTypescript)
            )
                return {
                    loaded: [],
                    files: {
                        [filePath]: {
                            status: "skipped",
                            reason: isTypescript ? "JS file in TS environment" : "TS file in JS environment",
                        },
                    },
                };

            try {
                const file = require(filePath).default;
                return {
                    loaded: [file],
                    files: {
                        [filePath]: {
                            status: "loaded",
                            index: 0,
                        },
                    },
                };
            } catch (err) {
                this.logger.error(err);
                return {
                    loaded: [],
                    files: {
                        [filePath]: {
                            status: "errored",
                            reason: err.message,
                        },
                    },
                };
            }
        }
    }

    private async loadCommands() {
        const commands = await Promise.all(
            this.botOptions.commands.map(async (command) => await this.load<AnyCommand>(command)),
        );
        const loadedCommands = commands.flatMap((e) => e.loaded);
        const loadedCommandsData: {
                name: string;
                type: string;
                status: string;
                loadStatus: string;
                filePath: string;
            }[] = [],
            commandFiles = commands.flatMap((e) => Object.entries(e.files));

        for (let i = 0; i < loadedCommands.length; i++) {
            try {
                const command = loadedCommands[i],
                    filePath = commandFiles.find((e) => e[1].index === i)![0];
                if (command.isIDBased()) {
                    if (command.isSelectMenuCommand()) this.commands.stores.selectMenu.set(command.id, command);
                    else if (command.isButtonCommand()) this.commands.stores.button.set(command.id, command);
                    else if (command.isModalCommand()) this.commands.stores.modal.set(command.id, command);
                    loadedCommandsData.push({
                        name: command.id,
                        type: command.isSelectMenuCommand()
                            ? "Select Menu"
                            : command.isButtonCommand()
                            ? "Button"
                            : "Modal",
                        status: command.enabled ? green("Enabled") : red("Disabled"),
                        loadStatus: bgGreen(`${commandFiles[i][1].status}\n${commandFiles[i][1].reason ?? ""}`),
                        filePath,
                    });
                } else {
                    if (command.isSlashCommand()) this.commands.stores.slash.set(command.data.name, command);
                    else if (command.isContextMenuCommand()) {
                        if (command.isUserContextMenuCommand())
                            this.commands.stores.contextMenuUser.set(command.data.name, command);
                        else this.commands.stores.contextMenuMessage.set(command.data.name, command);
                    } else if (command.isMessageCommand()) {
                        this.commands.stores.message.set(command.data.name, command);
                        for (const name of Object.values(command.data.nameLocalizations ?? {}))
                            this.commands.stores.message.set(name, command);
                        for (const alias of command.data.aliases ?? [])
                            this.commands.stores.message.set(alias, command);
                    }
                    loadedCommandsData.push({
                        name: command.data.name,
                        type: command.isSlashCommand()
                            ? "Slash"
                            : command.isContextMenuCommand()
                            ? "Context Menu"
                            : "Message",
                        status: command.enabled ?? true ? green("Enabled") : red("Disabled"),
                        loadStatus: bgGreen(`${commandFiles[i][1].status}\n${commandFiles[i][1].reason ?? ""}`),
                        filePath,
                    });
                }
            } catch (err) {
                this.logger.error(err);
                loadedCommandsData.push({
                    name: path.basename(commandFiles[i][0], path.extname(commandFiles[i][0])),
                    type: italic("Unknown"),
                    status: red("errored"),
                    loadStatus: bgRed(`${commandFiles[i][1].status}\n${commandFiles[i][1].reason ?? ""}`),
                    filePath: commandFiles[i][0],
                });
            }
        }

        for (const [filePath, command] of commandFiles) {
            if (command.status === "loaded") continue;
            loadedCommandsData.push({
                name: path.basename(filePath, path.extname(filePath)),
                type: italic("Unknown"),
                status: blackBright("Unknown"),
                loadStatus:
                    command.status === "skipped"
                        ? bgYellow(`${command.status}\n${command.reason ?? ""}`)
                        : bgRed(`${command.status}\n${command.reason ?? ""}`),
                filePath,
            });
        }

        if (this.botOptions.debug) {
            this.logger.debug(" ");
            this.logger.table(
                [
                    ["Command Name", "Type", "Status", "Load Status", "File Path"].map((h) => bold(h)),
                    ...loadedCommandsData.map((e) => [e.name, e.type, e.status, e.loadStatus, e.filePath]),
                    [
                        bold("Total"),
                        ...Object.entries(
                            Object.values(commands).reduce(
                                (acc, e) => {
                                    Object.values(e.files).forEach((e) => acc[e.status]++);
                                    return acc;
                                },
                                { loaded: 0, skipped: 0, errored: 0 },
                            ),
                        ).map(([k, v]) => `${k === "loaded" ? green(k) : k === "skipped" ? yellow(k) : red(k)}: ${v}`),
                        "",
                    ],
                ],
                {
                    header: {
                        content: magenta("Loaded Commands"),
                    },
                },
            );
        }
    }

    private async loadEvents() {
        const events = await Promise.all(
            this.botOptions.events.map(async (event) => await this.load<Event<any>>(event)),
        );
        const loadedEvents = events.flatMap((e) => e.loaded);

        for (const event of loadedEvents) {
            try {
                if (!(event.enabled ??= true)) continue;
                if (event.once)
                    this.once(event.name, async (...args) => {
                        for (const prerun of this.plugins.events[event.name]?.prerun ?? []) await prerun(...args);
                        const result = await event.run.bind(event)(...args);
                        for (const postrun of this.plugins.events[event.name]?.postrun ?? [])
                            await postrun(result, ...args);
                    });
                else
                    this.on(event.name, async (...args) => {
                        for (const prerun of this.plugins.events[event.name]?.prerun ?? []) await prerun(...args);
                        const result = await event.run.bind(event)(...args);
                        for (const postrun of this.plugins.events[event.name]?.postrun ?? [])
                            await postrun(result, ...args);
                    });
            } catch (err) {
                this.logger.error(err);
            }
        }

        if (this.botOptions.debug) {
            this.logger.debug(" ");
            this.logger.table(
                [
                    ["Event Name", "Event", "Status", "Load Status", "File Path"].map((h) => bold(h)),
                    ...events.flatMap((e, i) =>
                        Object.entries(e.files).map(([file, { status, reason, index }]) => [
                            events[i].loaded[index ?? -1]?.readableName ??
                                events[i].loaded[index ?? -1]?.name ??
                                path.basename(file, path.extname(file)),
                            events[i].loaded[index ?? -1]?.name ?? path.basename(file, path.extname(file)),
                            events[i].loaded[index ?? -1]
                                ? events[i].loaded[index ?? -1]?.enabled
                                    ? green("Enabled")
                                    : red("Disabled")
                                : blackBright("Unknown"),
                            status === "loaded"
                                ? bgGreen(`${status}\n${reason ?? ""}`)
                                : status === "skipped"
                                ? bgYellow(`${status}\n${reason ?? ""}`)
                                : bgRed(`${status}\n${reason ?? ""}`),
                            file,
                        ]),
                    ),
                    [
                        bold("Total"),
                        ...Object.entries(
                            Object.values(events).reduce(
                                (acc, e) => {
                                    Object.values(e.files).forEach((e) => acc[e.status]++);
                                    return acc;
                                },
                                { loaded: 0, skipped: 0, errored: 0 },
                            ),
                        ).map(([k, v]) => `${k === "loaded" ? green(k) : k === "skipped" ? yellow(k) : red(k)}: ${v}`),
                        "",
                    ],
                ],
                {
                    header: {
                        content: magenta("Loaded Events"),
                    },
                },
            );
        }
    }

    private async loadPlugins() {
        this.botOptions.plugins ??= [];
        const plugins = await Promise.all(
            this.botOptions.plugins.map(async (plugin) => await this.load<Plugin>(plugin)),
        );
        const loadedPlugins = plugins.flatMap((e) => e.loaded);

        for (const plugin of loadedPlugins) this.plugins.register(plugin);

        if (this.botOptions.debug) {
            this.logger.debug(" ");
            this.logger.table(
                [
                    ["Plugin Name", "Version", "Status", "Load Status", "File Path"].map((h) => bold(h)),
                    ...plugins.flatMap((e, i) =>
                        Object.entries(e.files).map(([file, { status, reason, index }]) => [
                            plugins[i].loaded[index ?? -1]?.name ?? path.basename(file, path.extname(file)),
                            plugins[i].loaded[index ?? -1]?.version ?? italic("Unknown"),
                            plugins[i].loaded[index ?? -1]
                                ? this.botOptions.enablePlugins === false
                                    ? plugins[i].loaded[index ?? -1]?.enabled === true
                                        ? green("Enabled - Forced")
                                        : red("Disabled")
                                    : plugins[i].loaded[index ?? -1]?.enabled
                                    ? green("Enabled")
                                    : red("Disabled")
                                : blackBright("Unknown"),
                            status === "loaded"
                                ? bgGreen(`${status}\n${reason ?? ""}`)
                                : status === "skipped"
                                ? bgYellow(`${status}\n${reason ?? ""}`)
                                : bgRed(`${status}\n${reason ?? ""}`),
                            file,
                        ]),
                    ),
                    [
                        bold("Total"),
                        ...Object.entries(
                            Object.values(plugins).reduce(
                                (acc, e) => {
                                    Object.values(e.files).forEach((e) => acc[e.status]++);
                                    return acc;
                                },
                                { loaded: 0, skipped: 0, errored: 0 },
                            ),
                        ).map(([k, v]) => `${k === "loaded" ? green(k) : k === "skipped" ? yellow(k) : red(k)}: ${v}`),
                        "",
                    ],
                ],
                {
                    header: {
                        content: magenta("Loaded Plugins"),
                    },
                },
            );
        }
    }
}

const parseOptions = <
    Configs extends Record<string, any>,
    Emojis extends Record<string, Emoji>,
    Colors extends Record<string, ColorResolvable>,
>(
    configs: Configs,
    options: BotOptions<Configs, Emojis, Colors>,
): PlainBotOptions<Configs, Emojis, Colors> => {
    const parsedOptions = {} as PlainBotOptions<Configs, Emojis, Colors>;

    for (const [key, value] of Object.entries(options)) {
        if (typeof value === "function") {
            parsedOptions[key] = (value as (configs: Configs) => any)(configs);
        } else {
            parsedOptions[key] = value;
        }
    }

    return parsedOptions;
};

export * from "./builders";
export * from "./managers";
export * from "./structures";
export * from "./typings";
